import { useCallback, useState, useEffect } from 'react';

export type EventFormStep = Readonly<'confirmGuest' | 'attendance' | 'contactInfo' | 'survey' | 'done' | 'disabled'>;

/**
 * Form type determines the navigation flow:
 * - `registrationAttendance`: Registration with attendance question (confirmGuest → contactInfo → attendance → survey → done)
 * - `registrationNoAttendance`: Registration without attendance question (confirmGuest → contactInfo → survey → done)
 * - `notAttending`: User marked not attending (confirmGuest → attendance → contactInfo → done)
 * - `form`: Survey-only form (confirmGuest → survey → done)
 */
type EventFormTypes = Readonly<'registrationAttendance' | 'registrationNoAttendance' | 'notAttending' | 'form'>;

type EventFormStepConfig = Readonly<{
  [key in EventFormStep]: {
    navigation: Record<
      EventFormTypes,
      {
        nextStep: EventFormStep | null;
        prevStep: EventFormStep | null;
      }
    >;
  };
}>;

export type GuestInfo = Readonly<{
  showConfirmation: boolean;
  hasSubmitted: boolean;
  guestDisplayName: string;
}>;

// IMPORTANT: When changing the order for structure of steps also
// look at handleNotAttending to ensure it returns the correct step
// when the guest selects No in the attendance question.
const EVENT_FORM_STEP_CONFIG: EventFormStepConfig = {
  confirmGuest: {
    navigation: {
      registrationAttendance: { nextStep: 'contactInfo', prevStep: null },
      notAttending: { nextStep: 'contactInfo', prevStep: null },
      registrationNoAttendance: { nextStep: 'contactInfo', prevStep: null },
      form: { nextStep: 'survey', prevStep: null },
    },
  },
  contactInfo: {
    navigation: {
      registrationAttendance: { nextStep: 'attendance', prevStep: null },
      notAttending: { nextStep: 'attendance', prevStep: null },
      registrationNoAttendance: { nextStep: 'survey', prevStep: null },
      form: { nextStep: null, prevStep: null },
    },
  },
  attendance: {
    navigation: {
      registrationAttendance: { nextStep: 'survey', prevStep: 'contactInfo' },
      notAttending: { nextStep: 'done', prevStep: 'contactInfo' },
      registrationNoAttendance: { nextStep: null, prevStep: null },
      form: { nextStep: null, prevStep: null },
    },
  },
  survey: {
    navigation: {
      registrationAttendance: { nextStep: 'done', prevStep: 'attendance' },
      notAttending: { nextStep: null, prevStep: null },
      registrationNoAttendance: { nextStep: 'done', prevStep: 'contactInfo' },
      form: { nextStep: 'done', prevStep: null },
    },
  },
  done: {
    navigation: {
      registrationAttendance: { nextStep: null, prevStep: 'survey' },
      notAttending: { nextStep: null, prevStep: 'attendance' },
      registrationNoAttendance: { nextStep: null, prevStep: 'survey' },
      form: { nextStep: null, prevStep: 'survey' },
    },
  },
  disabled: {
    navigation: {
      registrationAttendance: { nextStep: null, prevStep: null },
      notAttending: { nextStep: null, prevStep: null },
      registrationNoAttendance: { nextStep: null, prevStep: null },
      form: { nextStep: null, prevStep: null },
    },
  },
};

const getInitialStep = (formType: EventFormTypes, isFormDisabled: boolean, guestInfo?: GuestInfo): EventFormStep => {
  if (isFormDisabled) {
    return 'disabled';
  }
  if (guestInfo?.showConfirmation) {
    return 'confirmGuest';
  }

  const initialStepMap: Record<EventFormTypes, EventFormStep> = {
    registrationAttendance: 'contactInfo',
    registrationNoAttendance: 'contactInfo',
    notAttending: 'attendance',
    form: 'survey',
  };
  return initialStepMap[formType];
};

export const useEventFormNav = (formType: EventFormTypes, isFormDisabled: boolean, guestInfo?: GuestInfo) => {
  const [step, setStep] = useState<EventFormStep>(getInitialStep(formType, isFormDisabled, guestInfo));
  const [currentFormType, setCurrentFormType] = useState<EventFormTypes>(formType);

  const processNavigation = useCallback(
    (direction: 'next' | 'prev') => {
      const currentStepConfig = EVENT_FORM_STEP_CONFIG[step].navigation[currentFormType];
      const nextStep = direction === 'next' ? currentStepConfig.nextStep : currentStepConfig.prevStep;
      if (nextStep) {
        setStep(nextStep);
      }
    },
    [currentFormType, step],
  );

  const handleNotAttending = useCallback(() => {
    // When user marks as not attending, skip directly to 'done' to avoid
    // showing them the attendance question again. The navigation config
    // supports the flow contactInfo → attendance → done, but we bypass it here.
    setCurrentFormType('notAttending');
    setStep('done');
  }, []);

  const handleAttending = useCallback(() => {
    // Switch back to the initial type if user switches from not attending -> attending
    setCurrentFormType(formType);
    const nextStep = EVENT_FORM_STEP_CONFIG[step].navigation[formType].nextStep;
    if (nextStep) {
      setStep(nextStep);
    }
  }, [formType, step]);

  const hasNextStep = EVENT_FORM_STEP_CONFIG[step].navigation[currentFormType].nextStep !== null;
  const hasPrevStep = EVENT_FORM_STEP_CONFIG[step].navigation[currentFormType].prevStep !== null;

  useEffect(() => {
    if (isFormDisabled && step !== 'disabled') {
      setStep('disabled');
    }
  }, [isFormDisabled, step]);

  return {
    step,
    setStep,
    processNavigation,
    hasNextStep,
    hasPrevStep,
    handleNotAttending,
    handleAttending,
  };
};
